import type { CSSProperties, ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../partage/api/client';
import type { DossierDe, JourDuCalendrier } from '../partage/api/types';
import { cles } from '../partage/relectures';
import { AUJOURDHUI_DANS_SA_CASE, echeanceDe, journeeEnBref, PREPARER_MA_JOURNEE } from '../partage/vocabulaire';
import { heureLisible, jourEnToutesLettres, moisEnLettres, numeroDeCase } from '../maintenant';
import { teinteDuProjet } from '../apparence';
import './Calendrier.css';

/**
 * Le Calendrier de l'Agenda, la vue qui s'ouvre (chantier `revue-design`, ticket 06, maquette
 * `design/agenda.html` ; avant, ticket 15 de retours-testeurs).
 *
 * Un mois sur un calendrier papier (chantier `agenda-papier`, maquette du carnet `agenda.html`) :
 * sept colonnes de même largeur, les cases tracées au crayon, les jours hors du mois hachurés,
 * aujourd'hui dans son galet, le jour choisi en vert. En tête, le mois et ses flèches, et à droite
 * le filtre par projet (passé par l'Agenda). Chaque jour montre ce qui s'y passe : les échéances
 * soulignées à la couleur de leur projet, les rendez-vous en étiquettes à leur heure, et la
 * journée préparée d'un mot écrit à la main (« Journée : 4 choses, dès 9 h 30 »). Les jours
 * passés s'éteignent. **Un clic sur aujourd'hui ou un jour à venir ouvre « Préparer ma journée »**, à
 * droite (l'Agenda le pose) ; le jour ouvert s'entoure.
 *
 * Tout vient du serveur (`GET /api/calendrier`) : la grille, l'ordre, les heures.
 *
 * Dans l'angle Agenda d'une feuille (chantier angles-du-projet, ticket 03) : une `portee`, que le
 * serveur filtre (la bulle ouverte et ce qu'elle contient, sans journée préparée), ni filtre ni
 * `onJour` : aucun jour ne se clique, « Préparer ma journée » reste à la page.
 */
export function Calendrier({
  mois,
  jour,
  portee = null,
  dossierRetenu,
  filtres,
  onMois,
  onJour,
}: {
  mois: string | null;
  jour: string | null;
  /** La bulle ouverte, dans l'angle Agenda ; `null` : tous les projets. */
  portee?: string | null;
  dossierRetenu: string | null;
  /** Le filtre par projet, à droite du mois. */
  filtres?: ReactNode;
  onMois: (mois: string) => void;
  /** Un jour qui se clique ouvre « Préparer ma journée » ; sans lui, aucun ne se clique. */
  onJour?: (jour: string) => void;
}) {
  const calendrier = useQuery({
    queryKey: portee ? cles.calendrierDe(mois, portee) : cles.calendrier(mois),
    queryFn: () => api.calendrier(mois, portee ?? undefined),
  });
  const donnees = calendrier.data;

  return (
    <div className="calendrier" data-tuto="agenda-calendrier">
      <div className="calendrier-tete">
        <button
          type="button"
          className="calendrier-fleche"
          aria-label="Le mois d’avant"
          disabled={!donnees}
          onClick={() => donnees && onMois(donnees.precedent)}
        >
          ‹
        </button>
        <h2>{donnees ? moisEnLettres(donnees.mois) : ' '}</h2>
        <button
          type="button"
          className="calendrier-fleche"
          aria-label="Le mois d’après"
          disabled={!donnees}
          onClick={() => donnees && onMois(donnees.suivant)}
        >
          ›
        </button>
        {filtres && <div className="calendrier-filtres">{filtres}</div>}
      </div>

      <div className="calendrier-jours" aria-hidden="true">
        {['Lun.', 'Mar.', 'Mer.', 'Jeu.', 'Ven.', 'Sam.', 'Dim.'].map((n) => (
          <span key={n} className="calendrier-nom-du-jour">
            {n}
          </span>
        ))}
      </div>
      <div className="calendrier-grille">
        {donnees?.jours.map((j) => (
          <Case
            key={j.jour}
            jour={j}
            dossierRetenu={dossierRetenu}
            choisi={j.jour === jour}
            onPreparer={onJour ? () => onJour(j.jour) : undefined}
          />
        ))}
      </div>
    </div>
  );
}

const teinte = (d: DossierDe): CSSProperties => ({
  ['--teinte' as string]: teinteDuProjet(d?.couleur),
});

function Case({
  jour: j,
  dossierRetenu,
  choisi,
  onPreparer,
}: {
  jour: JourDuCalendrier;
  dossierRetenu: string | null;
  choisi: boolean;
  /** Sans lui (l'angle Agenda d'une feuille), la case ne se clique pas. */
  onPreparer?: () => void;
}) {
  const garder = (d: DossierDe) => !dossierRetenu || d?.id === dossierRetenu;
  const echeances = j.echeances.filter((e) => garder(e.dossier));
  // Les rendez-vous dans l'ordre des heures : posés par le monde.
  const rendezVous = j.rendezVous.filter((r) => garder(r.dossier)).sort((a, b) => a.heure.localeCompare(b.heure));
  // La journée préparée, d'un mot : combien de choses, et dès quelle heure (la plus tôt).
  const taches = (j.journee?.taches ?? []).filter((t) => garder(t.dossier ?? null));
  const premiere = taches.map((t) => t.heure).sort()[0];
  // Aujourd'hui se prépare ici aussi (le lien « Ma journée » de « Maintenant » y mène).
  const sePrepare = onPreparer !== undefined && (j.aPreparer || j.estAujourdhui);

  const classes = [
    'calendrier-case',
    j.dansLeMois ? '' : 'calendrier-case--hors-du-mois',
    j.passe ? 'calendrier-case--passe' : '',
    j.estAujourdhui ? 'calendrier-case--aujourdhui' : '',
    sePrepare ? 'calendrier-case--a-preparer' : '',
    choisi ? 'calendrier-case--choisie' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const contenu = (
    <>
      <span className="calendrier-numero">
        <b>{numeroDeCase(j.jour)}</b>
        {/* Aujourd'hui se voit à son galet ; le mot reste pour qui lit la page sans la voir. */}
        {j.estAujourdhui && <em>{AUJOURDHUI_DANS_SA_CASE}</em>}
      </span>
      {echeances.map((e) => (
        <span key={`e-${e.bulleId}`} className="calendrier-item calendrier-item--echeance" style={teinte(e.dossier)}>
          {echeanceDe(e.titre)}
        </span>
      ))}
      {rendezVous.map((r) => (
        <span key={`r-${r.bulleId}`} className="calendrier-item calendrier-item--rdv" style={teinte(r.dossier)}>
          {heureLisible(r.heure)} · {r.titre}
        </span>
      ))}
      {taches.length > 0 && premiere && (
        <span className="calendrier-item calendrier-item--journee">{journeeEnBref(taches.length, heureLisible(premiere))}</span>
      )}
    </>
  );

  // Le passé est éteint : il ne se clique pas.
  return sePrepare ? (
    <button
      type="button"
      className={classes}
      onClick={onPreparer}
      // La case d'aujourd'hui, pour le tuto (chantier `aide`, ticket 05) : rien de visible.
      data-tuto={j.estAujourdhui ? 'agenda-aujourdhui' : undefined}
      aria-label={`${PREPARER_MA_JOURNEE}, ${jourEnToutesLettres(j.jour)}`}
      aria-pressed={choisi}
    >
      {contenu}
    </button>
  ) : (
    <div className={classes}>{contenu}</div>
  );
}
